import {
    OrderDocumentTypeEnum,
    OrderItem,
    ProductDocumentTypeEnum,
} from '@/eshop/gql/graphql'
import { k2CurrencyToCc, useK2Setup } from '@/eshop/hooks/useK2Setup'
import { useFormatPrice } from '@/eshop/utils/price'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { InlineLoader } from './UserOrder'
import { useMutation } from '@tanstack/react-query'
import { getOrderDocument, getProductDocument } from '@/eshop/queries'
import { downloadBase64File } from '@/eshop/utils/client'
import { Button } from '@/components/Button'
import OrderItemState from './ItemState'

interface Props {
    items: OrderItem[]
    currency?: string
}

export default function OrderItemsTable({ items, currency }: Props) {
    const { t } = useTranslation()
    const k2 = useK2Setup()
    const [downloadProgress, setDownloadProgress] = useState<
        Record<'batch' | 'invoice', any[]>
    >({
        batch: [],
        invoice: [],
    })

    const formatPrice = useFormatPrice(k2CurrencyToCc[currency!], '', true)

    const { mutate: getBatchDocument } = useMutation({
        mutationKey: ['batches', k2.languageId],
        mutationFn: ({ batchId }: { batchId: number; name: string }) =>
            getProductDocument()({
                lang: k2.languageId,
                batchId,
                typeOfDoc: ProductDocumentTypeEnum.Certificate,
            }).toPromise(),
        onMutate: ({ batchId }) => {
            setDownloadProgress((state) => ({
                ...state,
                batch: [...state.batch, batchId],
            }))
        },
        onSettled: (res, _, { batchId, name }) => {
            setDownloadProgress((state) => ({
                ...state,
                batch: state.batch.filter((x) => x !== batchId),
            }))
            res?.match({
                Ok: (r) => {
                    downloadBase64File(name, r.productDocument.resultBase64!)
                },
                Error: () => {},
            })
        },
    })

    const { mutate: downloadInvoiceDocument } = useMutation({
        mutationKey: ['orderdocument'],
        mutationFn: (invoiceOutRID: string) =>
            getOrderDocument()({
                typeOfDoc: OrderDocumentTypeEnum.Invoice,
                lang: k2.languageId,
                invoiceOutRID,
            }).toPromise(),
        onMutate: (rid) => {
            setDownloadProgress((state) => ({
                ...state,
                invoice: [state.invoice, rid],
            }))
        },
        onSettled: (data, _, rid) => {
            setDownloadProgress((state) => ({
                ...state,
                invoice: state.invoice.filter((x) => x !== rid),
            }))
            data?.match?.({
                Ok: (v) => {
                    v?.orderDocument?.resultBase64 &&
                        downloadBase64File(rid, v.orderDocument.resultBase64)
                },
                Error: () => {},
            })
        },
    })

    if (!items || items.length === 0) {
        return (
            <table className="table">
                <tr>
                    <td colSpan={7}>
                        <div className="text-center">
                            {t('eshop.usersettings.orders.noItems')}
                        </div>
                    </td>
                </tr>
            </table>
        )
    }

    return (
        <table className="table">
            <thead>
                <tr className="text-blue-dark">
                    <th>{t('eshop.usersettings.orders.product')}</th>
                    <th>{t('eshop.usersettings.orders.catalogueNo')}</th>
                    <th>{t('eshop.usersettings.orders.unit')}</th>
                    <th className="text-right">
                        {t('eshop.usersettings.orders.price')}
                    </th>
                    <th>{t('eshop.usersettings.orders.state')}</th>
                    <th>{t('eshop.usersettings.orders.batch')}</th>
                </tr>
            </thead>
            <tbody>
                {items?.map((i) => (
                    <tr
                        className="text-md"
                        key={`${i?.articleAbbr}-${i?.quantityUMCalc}`}
                    >
                        <td style={{ wordBreak: 'break-word' }}>
                            <span className="text-sm break-words">
                                {i?.articleSortingNameLang}
                            </span>
                            <br />
                            <span className="text-xs">
                                {i?.articleCfQuality}
                            </span>
                        </td>
                        <td
                            className="text-sm"
                            style={{ wordBreak: 'break-word' }}
                        >
                            {i?.articleAbbr}
                        </td>
                        <td>
                            <span className="w-fit text-sm flex">
                                {i?.quantityUMCalc}x {i?.articleUnit}
                            </span>
                        </td>
                        <td className="text-right">
                            <div>
                                {formatPrice(i?.invoiceTotalPriceGrossCCalc)}
                            </div>
                            <div className="text-sm">
                                {formatPrice(i?.invoiceTotalPriceNetCCalc)}
                            </div>
                        </td>
                        <td className="h-full">
                            <span className="flex flex-col justify-center items-start h-full">
                                {i?.itemStatus && (
                                    <OrderItemState
                                        className="my-1"
                                        status={i?.itemStatus!}
                                    />
                                )}
                                {!!i?.invoiceOutRID &&
                                    i?.invoiceOutRID?.length > 1 && (
                                        <Button
                                            size="xs"
                                            color="secondary"
                                            onClick={() =>
                                                downloadInvoiceDocument(
                                                    i.invoiceOutRID!
                                                )
                                            }
                                            disabled={downloadProgress.invoice.includes(
                                                i.invoiceOutRID
                                            )}
                                        >
                                            <InlineLoader
                                                isLoading={downloadProgress.invoice.includes(
                                                    i.invoiceOutRID
                                                )}
                                                className="mx-10"
                                            >
                                                {t(
                                                    'eshop.usersettings.orders.downloadInvoice'
                                                )}
                                            </InlineLoader>
                                        </Button>
                                    )}
                            </span>
                        </td>
                        <td style={{ wordBreak: 'break-word' }}>
                            {!!i?.batchAbbr && !!i?.batchId && (
                                <div className="flex flex-col text-sm">
                                    <InlineLoader
                                        isLoading={downloadProgress.batch.includes(
                                            i.batchId
                                        )}
                                    >
                                        <span
                                            onClick={() =>
                                                getBatchDocument({
                                                    batchId: i.batchId!,
                                                    name: i.batchAbbr!,
                                                })
                                            }
                                            className="link"
                                        >
                                            {i.batchAbbr.split(' ')?.[0]}
                                        </span>
                                    </InlineLoader>
                                </div>
                            )}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    )
}
